/* Creator mode (snippets/creator-mode.liquid, sections/creator-data.liquid; docs/60-affiliate).
   A creator's link lands in the shop with ?mw_c=<handle>&ref=<handle> and the code already on the
   cart. mw_c is ours; ref feeds Shopify's customer journey, and Shopify's pixel manager removes it
   from the address when the shopper arrives through /discount/… (measured 09.10), so it is only
   the fallback.
   1. Fetch the creator's pieces once (/pages/c/<handle>?view=data) and keep them for the visit
      in sessionStorage — the offer the shopper clicked, nothing else, gone with the tab.
   2. Show the bar on every page and the campaign plate on the front page.
   3. Ask /cart.js what the code does (audit-kode M3): applicable → «✓ i kurven»; on the cart but
      the cart is empty → also «✓» (Shopify only judges a code against items); on the cart but not
      applicable to what is in it → no promise at all; not on the cart → «Brug … rabat», which ADDS
      the code to what is there (same rule as assets/cart.js).
   4. While the code counts, every price the code covers gets «160,65 kr med …s kode».
   5. With marketing consent, _mw_ref=<handle> goes on the cart as a second way to credit the
      creator (audit-kode M4; same rule as assets/creator.js). */

const KEY = 'mw:creator';
const root = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
const cartUrl = () => (window.Munkwell?.routes?.cart || '/cart').replace(/\/$/, '');

function readState() {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) || 'null');
  } catch {
    return null;
  }
}

function writeState(next) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* Private mode: the pieces still show on this page. */
  }
}

async function fetchCreator(handle) {
  const response = await fetch(`${root()}pages/c/${encodeURIComponent(handle)}?view=data`, {
    headers: { Accept: 'text/html' },
  });
  if (!response.ok) throw new Error(response.statusText);
  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  const data = JSON.parse(doc.querySelector('[data-creator-data]')?.textContent || 'null');
  if (!data?.code) throw new Error('no creator');
  return {
    ...data,
    bar: doc.querySelector('[data-creator-bar-html]')?.innerHTML.trim() || '',
    plate: doc.querySelector('[data-creator-plate-html]')?.innerHTML.trim() || '',
    dismissed: false,
  };
}

async function readCart() {
  const response = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(response.statusText);
  return response.json();
}

async function updateCart(body) {
  const response = await fetch(`${cartUrl()}/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(response.statusText);
  return response.json();
}

function place(el, html, show) {
  if (!el) return;
  if (show && html) {
    if (!el.innerHTML.trim()) el.innerHTML = html;
    el.hidden = false;
  } else {
    el.hidden = true;
  }
  el.classList.remove('is-loading');
}

/* One polite live region for the whole page: «lagt i kurven», or that it failed. */
let live;
function announce(message) {
  if (!live) {
    live = document.createElement('div');
    live.className = 'visually-hidden';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    document.body.append(live);
  }
  live.textContent = '';
  setTimeout(() => (live.textContent = message), 100);
}

let state = readState();
/* 'applied' | 'ready' (code on an empty cart) | 'inactive' (on the cart, not for these items) | 'missing' */
let codeState = 'missing';
let busy = false;
const counts = () => codeState === 'applied' || codeState === 'ready';

function paintStates() {
  document.querySelectorAll('[data-cm-applied]').forEach((el) => (el.hidden = !counts()));
  document.querySelectorAll('[data-cm-apply]').forEach((el) => (el.hidden = codeState !== 'missing'));
  paintPrices();
}

async function check() {
  if (!state) return;
  try {
    const cart = await readCart();
    const entry = (cart.discount_codes || []).find((d) => d.code.toLowerCase() === state.code.toLowerCase());
    if (!entry) codeState = 'missing';
    else if (entry.applicable) codeState = 'applied';
    else codeState = cart.item_count ? 'inactive' : 'ready';
    paintStates();
    rememberRef(cart);
    return cart;
  } catch {
    /* Say nothing rather than promise a discount the cart may not hold. */
  }
}

function marketingAllowed() {
  try {
    return Boolean(window.Shopify?.customerPrivacy?.marketingAllowed?.());
  } catch {
    return false;
  }
}

async function rememberRef(cart) {
  if (!state?.handle || !marketingAllowed() || cart?.attributes?._mw_ref === state.handle) return;
  try {
    await updateCart({ attributes: { _mw_ref: state.handle } });
  } catch {
    /* Attribution by code still works. */
  }
}

async function apply(button) {
  if (!state || busy) return;
  busy = true;
  /* The button disappears when it works; focus moves to the confirmation beside it, so a keyboard
     or screen reader user is not dropped at the top of the page (audit-a11y T1). */
  const holder = button.closest('.mwcm-bar__text, .mwcm-plate__code');
  try {
    const cart = await readCart();
    const codes = (cart.discount_codes || []).map((d) => d.code).filter((c) => c.toLowerCase() !== state.code.toLowerCase());
    await updateCart({ discount: [...codes, state.code].join(',') });
    const updated = await check();
    if (updated) document.dispatchEvent(new CustomEvent('cart:updated', { detail: updated }));
    const confirmation = holder?.querySelector('[data-cm-applied]');
    if (confirmation && !confirmation.hidden) {
      confirmation.setAttribute('tabindex', '-1');
      confirmation.focus({ preventScroll: true });
    }
    announce(state.appliedLabel || confirmation?.textContent?.trim() || '');
  } catch {
    announce(state.errorLabel || 'Rabatten kunne ikke lægges i kurven. Prøv igen.');
  } finally {
    busy = false;
  }
}

/* «160,65 kr med Lonnies kode» under every price, from the amount the price snippet hands over
   (data-amount, minor units in the shown currency). Only while the code counts. */
const money = (minor) =>
  `${new Intl.NumberFormat('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(minor / 100)} kr`;

function paintPrice(price) {
  let line = price.querySelector(':scope > .price__creator');
  const amount = Number(price.dataset.amount);
  /* Only where the code works: a code can be limited to some collections (state.products). */
  const eligible = state && (!state.products || state.products.includes(Number(price.dataset.productId)));
  if (!counts() || !amount || !eligible) {
    line?.remove();
    return;
  }
  const after = Math.round((amount * (100 - state.percent)) / 100);
  const shown =
    'from' in price.dataset && state.fromLabel ? state.fromLabel.replace('%%PRICE%%', money(after)) : money(after);
  const text = `${shown} ${state.priceLabel}`;
  if (!line) {
    line = document.createElement('small');
    line.className = 'price__creator';
    price.append(line);
  }
  if (line.textContent !== text) line.textContent = text;
}

function paintPrices(scope = document) {
  scope.querySelectorAll('.price[data-amount]').forEach(paintPrice);
}

function wire() {
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const applyButton = target?.closest('[data-cm-apply]');
    if (applyButton) {
      event.preventDefault();
      apply(applyButton);
    }
    if (target?.closest('[data-cm-dismiss]')) {
      event.preventDefault();
      if (state) writeState({ ...state, dismissed: true });
      place(document.querySelector('[data-cm-home]'), '', false);
      /* The close button is gone; continue from the start of the page content (audit-a11y). */
      const main = document.getElementById('MainContent');
      main?.focus({ preventScroll: true });
    }
  });
  document.addEventListener('cart:updated', () => check());

  /* Prices drawn later (variant change, quick add, recommendations) get their line too. Only added
     elements that are or hold a price are painted, not the whole page on every change. */
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches('.price[data-amount]')) paintPrice(node);
        else if (node.querySelector('.price[data-amount]')) paintPrices(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}

async function start() {
  const params = new URLSearchParams(location.search);
  const ref = params.get('mw_c') || params.get('ref');
  if (ref && (!state || state.handle !== ref.toLowerCase())) {
    try {
      state = await fetchCreator(ref.toLowerCase());
      writeState(state);
    } catch {
      state = null;
    }
  }
  const bar = document.querySelector('[data-cm-bar]');
  const home = document.querySelector('[data-cm-home]');
  if (!state) {
    place(bar, '', false);
    place(home, '', false);
    bar?.removeAttribute('data-npop-off');
    return;
  }
  bar?.setAttribute('data-npop-off', '');
  place(bar, state.bar, true);
  place(home, state.plate, !state.dismissed);
  wire();
  await check();
  document.addEventListener('visitorConsentCollected', () => check());
}

start();
